import { useEffect, useRef, useState } from 'react';
import CommandPalette from '../components/commandPalette/CommandPalette';
import DiagramCanvas from '../components/diagram/DiagramCanvas';
import SchemaExplorer from '../components/explorer/SchemaExplorer';
import ResizeHandle from '../components/layout/ResizeHandle';
import Toolbar from '../components/layout/Toolbar';
import PropertiesPanel from '../components/properties/PropertiesPanel';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { navigateToDashboard } from '../App';
import { useDiagramViewStore } from '../store/useDiagramViewStore';
import { useWorkspaceStore } from '../store/useWorkspaceStore';
import { useProjectsStore } from '../store/useProjectsStore';
import { useSchemaStore } from '../store/useSchemaStore';

const LEFT_MIN = 180;
const LEFT_MAX = 480;
const RIGHT_MIN = 280;
const RIGHT_MAX = 680;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export default function Editor({ projectId }: { projectId: string }) {
  const hydrated = useProjectsStore((s) => s.hydrated);
  const hydrate = useProjectsStore((s) => s.hydrate);
  const getProject = useProjectsStore((s) => s.get);
  const loadProject = useSchemaStore((s) => s.loadProject);
  const dirty = useSchemaStore((s) => s.dirty);
  const save = useSchemaStore((s) => s.save);
  const loadedProjectId = useSchemaStore((s) => s.projectId);

  // Members without the "edit diagrams" permission get a view-only editor.
  const canEdit = useWorkspaceStore((s) => s.canEditDiagram('er', projectId));
  const setReadOnly = useDiagramViewStore((s) => s.setReadOnly);
  useEffect(() => {
    setReadOnly(!canEdit);
    return () => setReadOnly(false);
  }, [canEdit, setReadOnly]);

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [leftWidth, setLeftWidth] = useState(240);
  const [rightWidth, setRightWidth] = useState(384);
  const panelWide = useDiagramViewStore((s) => s.panelWide);
  const docsRequested = useDiagramViewStore((s) => s.docsRequested);

  // Asking for the explanation (About button, "Go to explanation" menu) opens the side panel if it was closed.
  useEffect(() => {
    if (docsRequested) setRightOpen(true);
  }, [docsRequested]);

  // The About tab's "Wider" button asks for a roomier panel while reading a long explanation.
  useEffect(() => {
    setRightWidth(panelWide ? RIGHT_MAX : 384);
  }, [panelWide]);
  useEffect(() => () => useDiagramViewStore.setState({ panelWide: false, focusedColumn: null, tourOpen: false, tourFocus: null }), []);
  const diagramRef = useRef<HTMLDivElement>(null);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (!hydrated) return;
    if (loadedProjectId === projectId) return;
    const project = getProject(projectId);
    if (!project) {
      navigateToDashboard();
      return;
    }
    loadProject(project);
  }, [hydrated, projectId, loadedProjectId, getProject, loadProject]);

  useEffect(() => {
    if (!dirty || !canEdit) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => save(), 800);
    return () => clearTimeout(autosaveTimer.current);
  }, [dirty, save, canEdit]);

  useKeyboardShortcuts(() => setCommandPaletteOpen(true));

  if (loadedProjectId !== projectId) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas text-sm text-gray-400 dark:bg-gray-950 dark:text-gray-500">
        Loading project...
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <Toolbar
        diagramRef={diagramRef}
        onOpenCommandPalette={() => setCommandPaletteOpen(true)}
        leftOpen={leftOpen}
        onToggleLeft={() => setLeftOpen((v) => !v)}
        rightOpen={rightOpen}
        onToggleRight={() => setRightOpen((v) => !v)}
      />

      <div className="flex flex-1 overflow-hidden">
        {leftOpen && (
          <>
            <fieldset disabled={!canEdit} className="contents">
              <SchemaExplorer width={leftWidth} />
            </fieldset>
            <ResizeHandle onResize={(dx) => setLeftWidth((w) => clamp(w + dx, LEFT_MIN, LEFT_MAX))} />
          </>
        )}

        <div className="relative flex-1 overflow-hidden">
          <DiagramCanvas containerRef={diagramRef} />
        </div>

        {rightOpen && (
          <>
            <ResizeHandle onResize={(dx) => setRightWidth((w) => clamp(w - dx, RIGHT_MIN, RIGHT_MAX))} />
            <fieldset disabled={!canEdit} className="contents">
              <PropertiesPanel width={rightWidth} />
            </fieldset>
          </>
        )}
      </div>

      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        diagramRef={diagramRef}
        onTriggerImport={() => setRightOpen(true)}
      />
    </div>
  );
}
